import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './ContentFilter.module.scss';
import { Select } from '../../../../components/forms/Select';
import { SelectItem, SelectItemTemplate, SelectViewProps } from '../../../../components/forms/Select/Select.view';
import classNames from 'classnames';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { ContentFilterFacebook } from '../ContentFilterFacebook';
import { SelectSourceItemTemplate, SelectSourcesValueTemplate } from '../SelectTemplate';
import { ContentFilterInstagram } from '../ContentFilterInstagram';
import { ContentFilterLinkedIn } from '../ContentFilterLinkedIn';
import { ContentFilterPinterest } from '../ContentFilterPinterest';
import { ContentFilterRss } from '../ContentFilterRss';
import { ImportSourceType, platformItems } from '../../../../constants/find-content';
import { WithChildren } from '../../../../models/children-props';
import { NoContentBox } from '../NoContentBox';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { mapInputSourceTypeToProvider } from '../TreeMenu/TreeMenu.controller';
import { useLocation } from 'react-router-dom';
import { FindContentContext } from '../FindContentProvider';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { SocialOAuthProvider } from '../../../../models/graphql';

export type ContentFilterViewProps = {
  useFormInstance: UseFormReturn;
};

export const ContentFilterSubFilterWrapper = (props: WithChildren & { showFilters: boolean }): JSX.Element => {
  const { children, showFilters } = props;
  const wrapperClasses = classNames('cell');
  const subFilterWrapperClasses = classNames('margin-top-small-8', 'margin-bottom-small-24', classes.SubFilterWrapper);

  return (
    <>
      {showFilters && (
        <div className={wrapperClasses}>
          <div className={subFilterWrapperClasses}>{children}</div>
        </div>
      )}
    </>
  );
};

export const ContentFilterView = (props: ContentFilterViewProps): JSX.Element => {
  const { useFormInstance } = props;
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const treeMenuHeaderClasses = classNames(classes.TreeMenuHeader, 'margin-bottom-small-16');

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  const location = useLocation();
  const { modalType, selectedMenuItem } = useContext(TreeMenuContext);
  const { loading, error, posts, profiles, hasSearched } = useContext(FindContentContext);

  const platformValue = useWatch({ name: 'platform', control: useFormInstance.control });
  // Used strictly to know when we are viewing the PN Boards search so we can shrink the platform form element
  const typeValue = useWatch({ name: 'type', control: useFormInstance.control });

  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    const selectProps = {
      ...props,
      index,
      item
    };

    return <SelectSourceItemTemplate key={index} {...selectProps} />;
  };

  const hasPosts = posts.length > 0;

  const firstConnectedFacebookPageProfileId = profiles.filter(
    p => p.__typename === 'FacebookSocialProfile' && p.connected && p.type === 'page'
  )[0]?.id;

  // Check to see on the selected platform, if the user has such an account.
  const hasProfile = useMemo(() => {
    switch (platformValue) {
      case SocialOAuthProvider.FACEBOOK:
        return profiles.findIndex(p => p.__typename === 'FacebookSocialProfile') !== -1;
      case SocialOAuthProvider.INSTAGRAM:
        return profiles.findIndex(p => p.__typename === 'InstagramSocialProfile') !== -1;
      case SocialOAuthProvider.LINKEDIN:
        return profiles.findIndex(p => p.__typename === 'LinkedInSocialProfile') !== -1;
      case SocialOAuthProvider.PINTEREST:
        return profiles.findIndex(p => p.__typename === 'PinterestSocialProfile') !== -1;
      default:
        return true;
    }
  }, [platformValue, profiles]);

  // This is used for when clicking on a save source, so we know which is being used
  // if we have a modal type, we do not set the value (i.e. don't change when modal is open)
  useEffect(() => {
    if (!modalType && selectedMenuItem?.__typename === 'ImportSource') {
      useFormInstance.setValue('platform', mapInputSourceTypeToProvider(selectedMenuItem.type));
    }
  }, [modalType, selectedMenuItem, useFormInstance]);

  // When the selected "platform" is changed, we reset all filters
  useEffect(() => {
    setShowFilters(false);
  }, [platformValue]);

  // Look for passing of context for RSS, this is triggered when Add Auto-Importer button is pressed
  useEffect(() => {
    const locationContext = (location.state as { context: string }) || undefined;

    if (locationContext && locationContext.hasOwnProperty('context') && locationContext.context === 'selectRSS') {
      useFormInstance.setValue('platform', ImportSourceType.RSS);
    }
  }, [location, useFormInstance]);

  const selectPlatformClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large,
    'small-3': width >= BreakpointsValuesEnum.large && typeValue === 'BOARD',
    [classes.InputWrapperQuadElement]: width >= BreakpointsValuesEnum.large && typeValue === 'BOARD'
  });

  return (
    <div className={'margin-top-small-32'}>
      <div className={treeMenuHeaderClasses}>Search for Content</div>
      <div>
        <div className={'grid-x margin-bottom-small-24'}>
          <div className={selectPlatformClasses}>
            <Select
              chooseLabel={'Select Platform'}
              hideDefaultSelectOption={true}
              label={'Choose Platform'}
              items={platformItems}
              name={'platform'}
              size={'medium'}
              rules={{}}
              useFormInstance={useFormInstance}
              itemTemplate={itemTemplate}
              valueTemplate={SelectSourcesValueTemplate}
              disabled={loading}
            />
          </div>
          {platformValue === SocialOAuthProvider.FACEBOOK && (
            <ContentFilterFacebook
              formInstance={useFormInstance}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              firstConnectedPageProfileId={firstConnectedFacebookPageProfileId}
            />
          )}
          {platformValue === SocialOAuthProvider.INSTAGRAM && (
            <ContentFilterInstagram
              formInstance={useFormInstance}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          )}
          {platformValue === SocialOAuthProvider.LINKEDIN && (
            <ContentFilterLinkedIn
              formInstance={useFormInstance}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          )}
          {platformValue === SocialOAuthProvider.PINTEREST && (
            <ContentFilterPinterest
              formInstance={useFormInstance}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          )}
          {platformValue === 'RSS' && (
            <ContentFilterRss
              formInstance={useFormInstance}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
            />
          )}
        </div>
        {!platformValue ? (
          <>
            <ContentFilterSubFilterWrapper showFilters={false}>
              <div />
            </ContentFilterSubFilterWrapper>
            <NoContentBox title={'Search results will appear here'} type={'NEW_SEARCH'} network={platformValue}>
              <div></div>
            </NoContentBox>
          </>
        ) : !hasProfile ? (
          <NoContentBox title={'Nothing Profile Title'} type={'NO_PROFILE'} network={platformValue}>
            <div>{/* @TODO: Copy */}</div>
          </NoContentBox>
        ) : !hasSearched && !hasPosts && !loading && !error ? (
          <NoContentBox title={'Search results will appear here'} type={'NEW_SEARCH'} network={platformValue}>
            <div></div>
          </NoContentBox>
        ) : (
          hasSearched &&
          !hasPosts &&
          !loading &&
          !error && (
            <NoContentBox title={'Nothing Found Title'} type={'NO_RESULTS'} network={platformValue}>
              <div>{/* @TODO: Copy */}</div>
            </NoContentBox>
          )
        )}
      </div>
    </div>
  );
};
